/**
 * ============================================================================
 * LIFETIME SUMMARY API — RTK Query endpoints for the deep-dive lifetime
 * tables (spec dive-deep-dcr-so R2; design "Architecture").
 * ============================================================================
 *
 * STREAMING CACHE: unlike the other endpoints (queryFn = full accessor call),
 * these entries START EMPTY and are filled by `onCacheEntryAdded`, which runs
 * the accessor's page chain and appends each page via updateCachedData. The
 * first page therefore renders while the rest load (R2.2). Entry fields:
 *
 *   rows/groups     — accumulated records, in wire page order
 *   pagesLoaded     — progress for the "loading…" indicator
 *   complete        — chain terminated; export may enable (R6.2)
 *   error           — a mid-chain failure message; loaded rows stay (R2.4)
 *
 * RETRY: a failed chain parks on the last good cursor. The tab calls
 * requestLifetimeSummaryRetry(cacheKey); the parked chain resumes from that
 * cursor — completed pages are never re-requested (R2.4).
 *
 * ARGS: dataMode is always LIVE and reportingPeriod is the current week token
 * — feature constants (spec D4), supplied by the tab. reportingPeriod joins
 * the cache key so a Sunday-rollover mid-session simply starts a fresh entry.
 */

import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import { GetDcrSummaryApi } from 'src/accessors/GetDcrSummaryApi';
import { GetDcrServiceOrderSummaryApi } from 'src/accessors/GetDcrServiceOrderSummaryApi';
import { PaginationChainError } from 'src/accessors/followPaginationChain';
import {
  DcrServiceOrderGroup,
  DcrSummaryRecord,
} from 'src/types/lifetimeSummary.types';

export interface LifetimeSummaryArgs {
  dashboardId: string;
  /** Current week token — a publish label, not a data scope (spec intro). */
  reportingPeriod: string;
}

/** The streamed cache entry shape shared by both endpoints. */
export interface StreamedEntry<R> {
  rows: R[];
  pagesLoaded: number;
  complete: boolean;
  lastRefreshedAt?: string;
  error?: string;
}

const emptyEntry = <R>(): StreamedEntry<R> => ({ rows: [], pagesLoaded: 0, complete: false });

// ──── Retry signals (module state; keyed by endpoint + cache args) ────

const retrySignals = new Map<string, () => void>();

const retryKey = (endpoint: string, args: LifetimeSummaryArgs) =>
  `${endpoint}|${args.dashboardId}|${args.reportingPeriod}`;

/** Resumes a parked chain (the tab's "Resume loading" action, R2.4). */
export function requestLifetimeSummaryRetry(endpoint: 'dcr' | 'so', args: LifetimeSummaryArgs): void {
  retrySignals.get(retryKey(endpoint, args))?.();
}

// ──── The shared streaming lifecycle ────

type StreamFn<R> = (
  args: LifetimeSummaryArgs,
  onPage: (rows: R[], pageNo: number, lastRefreshedAt: string) => void,
  startKey?: string,
) => Promise<void>;

/**
 * Runs the page chain into the cache entry, parking on failures until a retry
 * signal, and stopping silently when the cache entry is removed.
 */
async function streamIntoCache<R>(
  endpoint: 'dcr' | 'so',
  args: LifetimeSummaryArgs,
  stream: StreamFn<R>,
  lifecycle: {
    cacheDataLoaded: Promise<unknown>;
    cacheEntryRemoved: Promise<void>;
    updateCachedData: (recipe: (draft: StreamedEntry<R>) => void) => void;
  },
): Promise<void> {
  const { cacheDataLoaded, cacheEntryRemoved, updateCachedData } = lifecycle;
  try {
    await cacheDataLoaded;
  } catch {
    return; // entry removed before initialization — nothing to stream into
  }

  // Entry removal stops cache WRITES and unparks the loop; an in-flight
  // accessor chain is not aborted mid-request (reviewed, accepted): these
  // entries are retained forever (keepUnusedDataFor: Infinity), so removal
  // only happens at store teardown — threading an AbortSignal through the
  // accessors buys nothing in production.
  let removed = false;
  cacheEntryRemoved.then(() => {
    removed = true;
    retrySignals.get(retryKey(endpoint, args))?.(); // unpark so the loop exits
  });

  let resumeKey: string | undefined;
  // Set when a failure had no resume cursor: the NEXT chain restarts from
  // page one, so its first page REPLACES the stale rows (clear-then-fill).
  // Rows stay visible at failure time — R2.4 — and never double-append.
  let replaceOnFirstPage = false;
  while (!removed) {
    let firstPageOfAttempt = true;
    try {
      await stream(
        args,
        (rows, _pageNo, lastRefreshedAt) => {
          if (removed) return;
          const replace = replaceOnFirstPage && firstPageOfAttempt;
          firstPageOfAttempt = false;
          updateCachedData((draft) => {
            if (replace) {
              draft.rows = rows as typeof draft.rows;
              draft.pagesLoaded = 1;
            } else {
              draft.rows.push(...(rows as typeof draft.rows));
              draft.pagesLoaded += 1;
            }
            draft.lastRefreshedAt = lastRefreshedAt;
            draft.error = undefined;
          });
          replaceOnFirstPage = replace ? false : replaceOnFirstPage;
        },
        resumeKey,
      );
      if (!removed) {
        updateCachedData((draft) => {
          draft.complete = true;
          // A resumed chain can terminate without delivering a page (the
          // failed page succeeds empty) — never leave a stale error beside
          // complete: true.
          draft.error = undefined;
        });
      }
      return;
    } catch (error) {
      if (removed) return;
      // A chain error carries the last good cursor; any other failure (e.g.
      // the runaway-chain guard) has none — the retry restarts from page one
      // and its first page replaces the stale rows. Loaded rows stay VISIBLE
      // through the failure (R2.4); nothing is wiped at failure time.
      resumeKey = error instanceof PaginationChainError ? error.resumeKey : undefined;
      replaceOnFirstPage = resumeKey === undefined;
      updateCachedData((draft) => {
        draft.error = error instanceof Error ? error.message : String(error);
      });
      // Park until the tab asks to resume (or the entry is removed).
      await new Promise<void>((resolve) => {
        retrySignals.set(retryKey(endpoint, args), resolve);
      });
      retrySignals.delete(retryKey(endpoint, args));
    }
  }
}

// ──── Endpoints ────

export const lifetimeSummaryApi = createApi({
  reducerPath: 'lifetimeSummaryApi',
  baseQuery: fakeBaseQuery<{ status: number; data: unknown }>(),
  keepUnusedDataFor: Infinity, // data changes nightly; header shows lastRefreshedAt
  refetchOnFocus: false,
  endpoints: (builder) => ({
    getLifetimeDcrSummary: builder.query<StreamedEntry<DcrSummaryRecord>, LifetimeSummaryArgs>({
      // The entry starts empty; onCacheEntryAdded streams the pages in.
      queryFn: async () => ({ data: emptyEntry<DcrSummaryRecord>() }),
      onCacheEntryAdded: async (args, lifecycle) =>
        streamIntoCache<DcrSummaryRecord>(
          'dcr',
          args,
          (a, onPage, startKey) =>
            GetDcrSummaryApi.streamAllPages(
              { dashboardId: a.dashboardId, reportingPeriod: a.reportingPeriod, dataMode: 'LIVE' },
              onPage,
              startKey,
            ),
          lifecycle,
        ),
    }),
    getLifetimeSoSummary: builder.query<StreamedEntry<DcrServiceOrderGroup>, LifetimeSummaryArgs>({
      queryFn: async () => ({ data: emptyEntry<DcrServiceOrderGroup>() }),
      onCacheEntryAdded: async (args, lifecycle) =>
        streamIntoCache<DcrServiceOrderGroup>(
          'so',
          args,
          (a, onPage, startKey) =>
            GetDcrServiceOrderSummaryApi.streamAllPages(
              { dashboardId: a.dashboardId, reportingPeriod: a.reportingPeriod, dataMode: 'LIVE' },
              onPage,
              startKey,
            ),
          lifecycle,
        ),
    }),
  }),
});

export const { useGetLifetimeDcrSummaryQuery, useGetLifetimeSoSummaryQuery } = lifetimeSummaryApi;
